import { useState } from "react"
import { Team } from "@/model/Team"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronUp } from "lucide-react"

interface LeaderboardProps {
  teams: Team[]
}

export function Leaderboard({ teams }: LeaderboardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const displayedTeams = isExpanded ? teams : teams.slice(0, 5)
  const hiddenCount = teams.length - 5

  const scrollToTeam = (teamId: string) => {
    const teamElement = document.getElementById(`team-${teamId}`)
    if (teamElement) {
      teamElement.scrollIntoView({ behavior: 'smooth', block: 'start' })

      // Add highlight effect
      teamElement.classList.add('ring-2', 'ring-primary', 'ring-opacity-75')
      setTimeout(() => {
        teamElement.classList.remove('ring-2', 'ring-primary', 'ring-opacity-75')
      }, 2000) // Remove highlight after 2 seconds
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>🏆 Leaderboard</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {displayedTeams.map((team, index) => (
            <div
              key={team.id}
              className="flex items-center justify-between rounded border p-2 hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => scrollToTeam(team.id)}
            >
              <div className="flex items-center space-x-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-xs">{team.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-sm">{team.score.toFixed(1)}</p>
              </div>
            </div>
          ))}

          {hiddenCount > 0 && (
            <div className="pt-1">
              {!isExpanded ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(true)}
                  className="w-full h-8 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown className="mr-1 h-3 w-3" />
                  Show {hiddenCount} more
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(false)}
                  className="w-full h-8 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronUp className="mr-1 h-3 w-3" />
                  Show less
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
